/**
 * Parser del subset de markdown que emite Phi → AST plano (fase 12 del plan
 * docs/PLAN_PHI_NIVEL3.md).
 *
 * Por qué propio y no `marked`: el AST se renderiza con componentes Vue
 * (AiMarkdown), así que NO hace falta `v-html` ni sanitizar HTML — el texto del
 * modelo nunca se interpreta como markup. Además no suma dependencias al build
 * (vue-cli 4 / webpack 4) y tolera el markdown INCOMPLETO del streaming.
 *
 * Subset soportado (el system prompt del sidecar restringe la salida a esto):
 *   bloque : párrafo, #..#### , listas - * + , listas 1. , tabla GFM,
 *            ``` código ```, --- , > cita
 *   inline : **negrita**, *itálica* / _itálica_, ***ambas***, `código`,
 *            escapes \* \_ \`
 * Todo lo demás queda como TEXTO LITERAL: nunca se pierde contenido. Límite
 * conocido y aceptado: el énfasis mezclado asimétrico (`**a *b***`) no anida —
 * sale la negrita y el asterisco sobrante queda visible (no es CommonMark
 * completo; resolverlo pide una pila de delimitadores que no vale el costo).
 *
 * Contrato de streaming (una respuesta llega por pedazos):
 *   - un `**` sin cerrar queda literal hasta que cierre;
 *   - una tabla sin su fila de guiones todavía es un párrafo;
 *   - una fila de tabla a medio llegar se renderiza con las celdas que haya;
 *   - una cerca ``` sin cerrar ya se muestra como bloque de código.
 *
 * Nodos de bloque:
 *   { type: "paragraph", children }        { type: "heading", level, children }
 *   { type: "list", ordered, items: [children] }
 *   { type: "table", head: [children], align: [], rows: [[children]] }
 *   { type: "code", value }               { type: "quote", children }
 *   { type: "hr" }
 * Nodos inline:
 *   { type: "text", value }  { type: "strong", children }
 *   { type: "em", children } { type: "codeSpan", value }
 */

const ESCAPABLE = "\\*_`|#->";
const MAX_HEADING_LEVEL = 4;

const HR_RE = /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/;
const HEADING_RE = /^ {0,3}(#{1,6})\s+(.*)$/;
const FENCE_RE = /^ {0,3}```/;
const QUOTE_RE = /^ {0,3}>\s?(.*)$/;
const BULLET_RE = /^\s*[-*+]\s+(.*)$/;
const ORDERED_RE = /^\s*\d+[.)]\s+(.*)$/;
const TABLE_ROW_RE = /^\s*\|.*$/;
// Título PEGADO al final de una frase: "…(julio a diciembre).### Ventas Netas".
// Visto en una respuesta real; sin esto el "###" se muestra crudo. El prefijo
// tiene que terminar en algo que no sea `#` ni espacio para no partir un título
// legítimo ("### Título") ni una línea de sólo almohadillas.
const GLUED_HEADING_RE = /^(.*[^#\s])\s*(#{1,4}\s+\S.*)$/;
// Fila separadora de una tabla GFM: |---|:--:| … (al menos un guion).
const TABLE_DIVIDER_RE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

/**
 * Separa en su propia línea los títulos que el modelo pega al final de una
 * frase. Los bloques de código quedan intactos: ahí un "#" es un comentario.
 * @param {Array<string>} lines
 * @returns {Array<string>}
 */
function splitGluedHeadings(lines) {
	const result = [];
	let insideFence = false;
	lines.forEach((line) => {
		if (FENCE_RE.test(line)) {
			insideFence = !insideFence;
			result.push(line);
			return;
		}
		const glued = insideFence ? null : line.match(GLUED_HEADING_RE);
		if (glued) {
			result.push(glued[1], "", glued[2]);
		} else {
			result.push(line);
		}
	});
	return result;
}

function isWordChar(char) {
	return Boolean(char) && /[\wÀ-ɏ]/.test(char);
}

/** Índice del cierre de un delimitador, ignorando el que abre pegado. */
function findClosing(text, marker, from) {
	let index = text.indexOf(marker, from);
	while (index !== -1) {
		// Contenido vacío (`****`) no es énfasis; y el cierre no puede venir
		// pegado a un espacio por la izquierda (`** texto **` no es negrita).
		if (index > from && text[index - 1] !== " ") {
			return index;
		}
		index = text.indexOf(marker, index + 1);
	}
	return -1;
}

/**
 * Tokeniza el contenido inline de una línea/celda.
 * @param {string} text
 * @returns {Array<object>} nodos inline (lista vacía si no hay texto)
 */
export function parseInline(text) {
	const nodes = [];
	let buffer = "";
	let index = 0;

	const flush = () => {
		if (buffer) {
			nodes.push({ type: "text", value: buffer });
			buffer = "";
		}
	};

	while (index < text.length) {
		const char = text[index];

		if (char === "\\" && ESCAPABLE.includes(text[index + 1])) {
			buffer += text[index + 1];
			index += 2;
			continue;
		}

		if (char === "`") {
			const end = text.indexOf("`", index + 1);
			if (end > index + 1) {
				flush();
				nodes.push({ type: "codeSpan", value: text.slice(index + 1, end) });
				index = end + 1;
				continue;
			}
		}

		// `***texto***` = negrita + itálica (el modelo lo usa para destacar un
		// dato). Va antes que `**` para que el marcador triple no se parta.
		if (text.startsWith("***", index)) {
			const end = findClosing(text, "***", index + 3);
			if (end !== -1) {
				flush();
				nodes.push({
					type: "strong",
					children: [
						{ type: "em", children: parseInline(text.slice(index + 3, end)) },
					],
				});
				index = end + 3;
				continue;
			}
		}

		if (text.startsWith("**", index) || text.startsWith("__", index)) {
			const marker = text.slice(index, index + 2);
			const end = findClosing(text, marker, index + 2);
			if (end !== -1) {
				flush();
				nodes.push({
					type: "strong",
					children: parseInline(text.slice(index + 2, end)),
				});
				index = end + 2;
				continue;
			}
		}

		// `_` sólo abre en borde de palabra: así `nombre_de_cuenta` no se
		// vuelve itálica (los nombres de cuentas del plan las traen).
		const opensEmphasis =
			(char === "*" || char === "_") &&
			text[index + 1] !== " " &&
			(char === "*" || !isWordChar(text[index - 1]));
		if (opensEmphasis) {
			const end = findClosing(text, char, index + 1);
			const closesOnWordBoundary =
				char === "*" || !isWordChar(text[end + 1]);
			if (end !== -1 && closesOnWordBoundary) {
				flush();
				nodes.push({
					type: "em",
					children: parseInline(text.slice(index + 1, end)),
				});
				index = end + 1;
				continue;
			}
		}

		buffer += char;
		index += 1;
	}

	flush();
	return nodes;
}

function splitTableRow(line) {
	const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
	return trimmed.split("|").map((cell) => cell.trim());
}

function alignmentsFrom(dividerLine) {
	return splitTableRow(dividerLine).map((cell) => {
		if (cell.startsWith(":") && cell.endsWith(":")) {
			return "center";
		}
		if (cell.endsWith(":")) {
			return "right";
		}
		return "left";
	});
}

function parseTable(lines, start) {
	const head = splitTableRow(lines[start]).map(parseInline);
	const align = alignmentsFrom(lines[start + 1]);
	const rows = [];
	let index = start + 2;
	while (index < lines.length && TABLE_ROW_RE.test(lines[index])) {
		const cells = splitTableRow(lines[index]).map(parseInline);
		// Fila corta (la última mientras streamea) se completa con celdas
		// vacías; una fila con celdas de más se conserva entera.
		while (cells.length < head.length) {
			cells.push([]);
		}
		rows.push(cells);
		index += 1;
	}
	return { node: { type: "table", head, align, rows }, next: index };
}

function parseList(lines, start, ordered) {
	const pattern = ordered ? ORDERED_RE : BULLET_RE;
	const items = [];
	let index = start;
	while (index < lines.length) {
		const match = lines[index].match(pattern);
		if (!match) {
			break;
		}
		items.push(parseInline(match[1]));
		index += 1;
	}
	// Sin anidación: una sublista indentada entra como ítem del mismo nivel
	// (se ve plano, no se pierde texto).
	return { node: { type: "list", ordered, items }, next: index };
}

function parseFence(lines, start) {
	const collected = [];
	let index = start + 1;
	while (index < lines.length && !FENCE_RE.test(lines[index])) {
		collected.push(lines[index]);
		index += 1;
	}
	// Cerca sin cerrar (streaming): se cierra sola al final del texto.
	return {
		node: { type: "code", value: collected.join("\n") },
		next: index < lines.length ? index + 1 : index,
	};
}

function parseQuote(lines, start) {
	const collected = [];
	let index = start;
	while (index < lines.length) {
		const match = lines[index].match(QUOTE_RE);
		if (!match) {
			break;
		}
		collected.push(match[1]);
		index += 1;
	}
	return {
		node: { type: "quote", children: parseInline(collected.join("\n")) },
		next: index,
	};
}

function isBlockStart(line) {
	return (
		HR_RE.test(line) ||
		HEADING_RE.test(line) ||
		FENCE_RE.test(line) ||
		QUOTE_RE.test(line) ||
		BULLET_RE.test(line) ||
		ORDERED_RE.test(line) ||
		TABLE_ROW_RE.test(line) ||
		!line.trim()
	);
}

function parseParagraph(lines, start) {
	const collected = [lines[start]];
	let index = start + 1;
	while (index < lines.length && !isBlockStart(lines[index])) {
		collected.push(lines[index]);
		index += 1;
	}
	// Los saltos simples dentro del párrafo se preservan al renderizar
	// (white-space: pre-line), que es como se lee una respuesta de chat.
	return {
		node: { type: "paragraph", children: parseInline(collected.join("\n")) },
		next: index,
	};
}

/**
 * Parsea markdown a un AST de bloques.
 * @param {string} text texto (posiblemente parcial) emitido por el modelo
 * @returns {Array<object>} nodos de bloque; lista vacía si no hay contenido
 */
export function parseMarkdown(text) {
	if (!text) {
		return [];
	}
	const lines = splitGluedHeadings(
		String(text).replace(/\r\n?/g, "\n").split("\n")
	);
	const blocks = [];
	let index = 0;

	while (index < lines.length) {
		const line = lines[index];

		if (!line.trim()) {
			index += 1;
			continue;
		}
		if (FENCE_RE.test(line)) {
			const { node, next } = parseFence(lines, index);
			blocks.push(node);
			index = next;
			continue;
		}
		if (HR_RE.test(line)) {
			blocks.push({ type: "hr" });
			index += 1;
			continue;
		}
		const heading = line.match(HEADING_RE);
		if (heading) {
			blocks.push({
				type: "heading",
				level: Math.min(heading[1].length, MAX_HEADING_LEVEL),
				children: parseInline(heading[2]),
			});
			index += 1;
			continue;
		}
		if (QUOTE_RE.test(line)) {
			const { node, next } = parseQuote(lines, index);
			blocks.push(node);
			index = next;
			continue;
		}
		// Tabla: sólo cuando ya llegó la fila de guiones (si no, es párrafo).
		if (
			TABLE_ROW_RE.test(line) &&
			index + 1 < lines.length &&
			TABLE_DIVIDER_RE.test(lines[index + 1]) &&
			lines[index + 1].includes("-")
		) {
			const { node, next } = parseTable(lines, index);
			blocks.push(node);
			index = next;
			continue;
		}
		if (BULLET_RE.test(line)) {
			const { node, next } = parseList(lines, index, false);
			blocks.push(node);
			index = next;
			continue;
		}
		if (ORDERED_RE.test(line)) {
			const { node, next } = parseList(lines, index, true);
			blocks.push(node);
			index = next;
			continue;
		}
		const { node, next } = parseParagraph(lines, index);
		blocks.push(node);
		index = next;
	}

	return blocks;
}
